import { Controller } from '@hotwired/stimulus'
import { StreamElement, TurboBeforeStreamRenderEvent, TurboSubmitEndEvent } from '@hotwired/turbo'
import { resolveIdentityPrefixForPath } from '../../utils'

// Lets `document.addEventListener` type the events this controller dispatches
declare global {
  interface DocumentEventMap {
    'stream-manager:render-activity': StreamEvent
    'stream-manager:render-priority': StreamEvent
    'stream-manager:render-record': StreamEvent
    'stream-manager:render-submissions-table': StreamEvent
  }
}

export type StreamName = 'activity' | 'priority' | 'record' | 'submission-fields' | 'submissions-table'

export interface StreamEventDetail {
  upstreamEvent: TurboBeforeStreamRenderEvent
  originalRender: (currentElement: StreamElement) => Promise<void>
  streamElement: StreamElement
  fragment: HTMLElement
}

export interface StreamEvent extends Event {
  detail: StreamEventDetail
}

export default class StreamManagerController extends Controller<HTMLBodyElement> {
  static nullIdentityUrlRef = '000'

  connect() {
    document.addEventListener('turbo:before-stream-render', this.#handleIncomingStreamAndRoute)
    document.addEventListener('turbo:submit-end', this.#handleSubmitEnd)
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.#handleIncomingStreamAndRoute)
    document.removeEventListener('turbo:submit-end', this.#handleSubmitEnd)
  }

  #handleIncomingStreamAndRoute = (event: TurboBeforeStreamRenderEvent) => {
    const render = event.detail.render
    const target = event.target as EventTarget & { action: string }

    // We don't do any additional processing for removing an element
    if (target?.action === 'remove') return

    event.detail.render = async streamElement => {
      const template = event.detail.newStream.children[0] as HTMLTemplateElement
      const fragment = template.content.children[0] as HTMLElement
      const streamName = fragment?.dataset.streamName as StreamName

      // Scan for identity prefix placeholders and contextualize them before any handling happens
      fragment && this.#contextualizeUrls(fragment)

      switch (streamName) {
        case 'activity':
        case 'record':
        case 'priority':
        case 'submissions-table':
          this.dispatch(`render-${streamName}`, {
            detail: {
              upstreamEvent: event,
              originalRender: render,
              streamElement,
              fragment,
            },
          } satisfies Partial<StreamEvent>)
          break
        default:
          // submission-fields
          return render(streamElement)
      }
    }
  }

  #handleSubmitEnd = (event: TurboSubmitEndEvent) => {
    if (event.detail.success) {
      setTimeout(() => {
        if (window.app.snackBar) {
          window.app.snackBar.removeAll()
        }
      }, 100)
    }
  }

  #contextualizeUrls(fragment: HTMLElement) {
    const nullRefLinksSelector = `a[href*='/i/${StreamManagerController.nullIdentityUrlRef}']`
    const links = fragment.matches(nullRefLinksSelector)
      ? ([fragment] as HTMLAnchorElement[])
      : fragment.querySelectorAll<HTMLAnchorElement>(nullRefLinksSelector)
    links.forEach(link => {
      link.href = resolveIdentityPrefixForPath(link.getAttribute('href'))
    })
  }
}
